import './App.css';

import { useCallback, useEffect, useState } from 'react';
import GoalsView from './components/GoalsView';
import LoginForm from './components/LoginForm';
import MainView from './components/MainView';
import {
  addWorkoutSet,
  createWorkout,
  deleteWorkoutSet,
  fetchGoal,
  fetchWorkouts,
  login as loginRequest,
  updateGoal,
  updateWorkout,
} from './services/apiClient';

function App() {
  const [session, setSession] = useState(null);
  const [currentView, setCurrentView] = useState('login');
  const [workouts, setWorkouts] = useState([]);
  const [goalText, setGoalText] = useState('');
  const [loginError, setLoginError] = useState('');
  const [dataError, setDataError] = useState('');
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);

  const isAuthenticated = Boolean(session);

  const loadDashboardData = useCallback(async (token) => {
    setIsLoadingData(true);
    setDataError('');
    try {
      const [workoutList, goal] = await Promise.all([
        fetchWorkouts(token),
        fetchGoal(token),
      ]);
      setWorkouts(workoutList);
      setGoalText(goal || '');
    } catch (error) {
      setDataError(error.message || 'データの読み込みに失敗しました');
    } finally {
      setIsLoadingData(false);
    }
  }, []);

  useEffect(() => {
    if (!session) {
      setWorkouts([]);
      setGoalText('');
      return;
    }
    loadDashboardData(session.token);
  }, [session, loadDashboardData]);

  const handleLogin = async ({ id, password }) => {
    setIsAuthenticating(true);
    setLoginError('');
    try {
      const result = await loginRequest({ id, password });
      const token = result?.token;
      if (!token) {
        throw new Error('ログイン情報の確認に失敗しました。');
      }
      setSession({ token });
      setCurrentView('main');
    } catch (error) {
      setSession(null);
      setLoginError(error.message || 'IDまたはパスワードが違います。');
    } finally {
      setIsAuthenticating(false);
    }
  };

  const handleLogout = () => {
    setSession(null);
    setCurrentView('login');
  };

  const handleGoalSave = async (text) => {
    if (!session) {
      throw new Error('ログイン状態が失われました。');
    }
    try {
      const saved = await updateGoal(session.token, text);
      setGoalText(saved);
      setDataError('');
    } catch (error) {
      setDataError(error.message || '目標の保存に失敗しました');
      throw error;
    }
  };

  const handleCreateWorkout = async (name) => {
    if (!session) {
      throw new Error('ログイン状態が失われました。');
    }
    try {
      const workout = await createWorkout(session.token, name);
      setWorkouts((prev) => [...prev, workout]);
      setDataError('');
      return workout;
    } catch (error) {
      setDataError(error.message || '種目の作成に失敗しました');
      throw error;
    }
  };

  const handleAddSet = async (workoutId, setInput) => {
    if (!session) {
      throw new Error('ログイン状態が失われました。');
    }
    try {
      const updatedWorkout = await addWorkoutSet(session.token, workoutId, setInput);
      setWorkouts((prev) =>
        prev.map((workout) => (workout.id === updatedWorkout.id ? updatedWorkout : workout))
      );
      setDataError('');
      return updatedWorkout;
    } catch (error) {
      setDataError(error.message || '記録の保存に失敗しました');
      throw error;
    }
  };

  const handleDeleteSet = async (workoutId, setId) => {
    if (!session) {
      throw new Error('ログイン状態が失われました。');
    }
    try {
      const updatedWorkout = await deleteWorkoutSet(session.token, workoutId, setId);
      setWorkouts((prev) =>
        prev.map((workout) => (workout.id === updatedWorkout.id ? updatedWorkout : workout))
      );
      setDataError('');
      return updatedWorkout;
    } catch (error) {
      setDataError(error.message || 'セットの削除に失敗しました');
      throw error;
    }
  };

  const handleGraphTypeUpdate = async (workoutId, graphType) => {
    if (!session) {
      throw new Error('ログイン状態が失われました。');
    }
    try {
      const updatedWorkout = await updateWorkout(session.token, workoutId, { graphType });
      setWorkouts((prev) =>
        prev.map((workout) => (workout.id === updatedWorkout.id ? updatedWorkout : workout))
      );
      setDataError('');
      return updatedWorkout;
    } catch (error) {
      setDataError(error.message || 'グラフ設定の更新に失敗しました');
      throw error;
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="app-shell">
        <LoginForm
          onLogin={handleLogin}
          errorMessage={loginError}
          isSubmitting={isAuthenticating}
        />
      </div>
    );
  }

  if (currentView === 'goals') {
    return (
      <div className="app-shell">
        <GoalsView
          initialText={goalText}
          onSave={handleGoalSave}
          onNavigateMain={() => setCurrentView('main')}
        />
      </div>
    );
  }

  return (
    <div className="app-shell">
      <MainView
        workouts={workouts}
        goalText={goalText}
        onNavigateGoals={() => setCurrentView('goals')}
        onLogout={handleLogout}
        onCreateWorkout={handleCreateWorkout}
        onAddSet={handleAddSet}
        onDeleteSet={handleDeleteSet}
        onUpdateGraphType={handleGraphTypeUpdate}
        isLoading={isLoadingData}
        errorMessage={dataError}
      />
    </div>
  );
}

export default App;
